import React, { useEffect } from 'react';
import { SAFETY_QUESTIONS } from '../../data/safetyQuestions';
import { HierarchySelector, DeleteImageModal } from './hierarchy';


export default function HierarchyStep({ data, value, onChange, setNewPhotoUploaded, lotoContext = {} }) {
    const lastSyncedTask = React.useRef(null);
    const hierarchy = data.hierarchy || {};
    // ...
    // Note: usages of combinedData below need to change to lotoContext

    const onderdeel_tasks = data.onderdeel_tasks || {};

    // Extract options based on current selection
    // Natural sort for Halls (Hal 1, Hal 2, ... Hal 10)

    // Use global onderdeel_tasks mapping instead of hierarchy-based tasks
    const halls = Object.keys(hierarchy)
        .filter(h => {
            // 1. Filter out technical codes D + digit
            if (h.match(/^\s*D\d+/i)) return false;

            // 2. Filter by TYPE if available (Prevent Lines from appearing in Hall dropdown)
            const item = hierarchy[h];
            // If type exists, it MUST be one of the allowed root types.
            // If type is missing, we fallback to name-based heuristic or allow it (risk of garbage).
            // Given the user report, stricter is better.
            if (item && item.type) {
                const t = item.type.toLowerCase();
                return t === 'hal' || t === 'gebouw' || t === 'buiten' || t === 'location';
            }

            // 3. Fallback: If no type, exclude things that look like lines
            if (h.toLowerCase().includes('lijn') || h.toLowerCase().includes('line')) return false;

            return true;
        })
        .sort((a, b) => {
            const numA = parseInt(a.replace(/\D/g, '')) || 0;
            const numB = parseInt(b.replace(/\D/g, '')) || 0;
            return numA - numB;
        });

    const lines = value.hall ? Object.keys(hierarchy[value.hall] || {}).sort() : [];
    const sublines = value.line ? Object.keys(hierarchy[value.hall]?.[value.line] || {}).sort() : [];

    // Calculate available components:
    // 1. Start with all global components from library
    // 2. Filter out those marked as inactive for this subline
    // 3. Add any legacy/custom components that exist directly in hierarchy but not in library
    const sublineData = value.subline ? (hierarchy[value.hall]?.[value.line]?.[value.subline] || {}) : {};
    const inactiveComponents = (sublineData._inactiveComponents || []).map(c => c.toLowerCase());
    const globalComponents = Object.keys(onderdeel_tasks);

    // Filter case-insensitive
    const activeGlobalComponents = globalComponents.filter(c => !inactiveComponents.includes(c.toLowerCase()));

    // Explicitly check for Molen/Machine-like critical components that might be mis-filtered
    // If 'molen' is in globalComponents but somehow got filtered (or casing mismatch), we want to see it?
    // Actually, trust the inactive list, but ensure we don't accidentally hide 'Molen' if 'molen' is inactive.
    // The case-insensitive check above handles 'Molen' vs 'molen'.

    // Also get any direct keys that are NOT metadata (start with _) and NOT already in global list
    // Check coverage case-insensitively
    const customComponents = Object.keys(sublineData)
        .filter(k =>
            !k.startsWith('_') &&
            !['docLink', 'docLinkElectro', 'docLinkManual'].includes(k) &&
            !globalComponents.some(gc => gc.toLowerCase() === k.toLowerCase())
        );

    const components = value.subline
        ? [...new Set([...activeGlobalComponents, ...customComponents])].sort((a, b) => {
            // Robust sort handling strict/loose
            return a.toLowerCase().trim().localeCompare(b.toLowerCase().trim());
        })
        : [];

    // If component is "Algemeen", show all tasks. Otherwise try to look up tasks (which will fail if mapping is missing)
    const tasks = value.component
        ? (value.component === 'Algemeen'
            ? Object.keys(onderdeel_tasks).sort()
            : (onderdeel_tasks[value.component] || []).sort((a, b) => a.localeCompare(b)))
        : [];

    const task_metadata = data.task_metadata || {};

    const handleChange = (field, val) => {
        // Reset children when parent changes
        const updates = { [field]: val };

        // Helper to get subline data
        const getSublineData = (h, l, sl) => data.hierarchy?.[h]?.[l]?.[sl] || {};

        if (field === 'hall') {
            updates.line = '';
            updates.subline = '';
            updates.component = '';
            updates.task = '';
            updates.sublineDocLink = '';
            updates.componentDocLink = '';
            updates.sopLink = '';
            updates.tools = [];
            updates.pbms = [];
            updates.taskImageUrls = [];
        } else if (field === 'line') {
            updates.subline = '';
            updates.component = '';
            updates.task = '';
            updates.sublineDocLink = '';
            updates.componentDocLink = '';
            updates.sopLink = '';
            updates.tools = [];
            updates.pbms = [];
            updates.taskImageUrls = [];
        } else if (field === 'subline') {
            updates.component = '';
            updates.task = '';
            updates.componentDocLink = '';
            updates.sopLink = '';
            updates.tools = [];
            updates.pbms = [];
            updates.taskImageUrls = [];

            // Extract Subline Doc Link
            const sublineData = getSublineData(value.hall, value.line, val);
            updates.sublineDocLink = sublineData.docLink || '';
            updates.sublineDocLinkElectro = sublineData.docLinkElectro || '';
            updates.sublineDocLinkManual = sublineData.docLinkManual || '';

        } else if (field === 'component') {
            updates.task = '';
            updates.sopLink = '';
            updates.tools = [];
            updates.pbms = [];
            updates.taskImageUrls = [];

            // Extract Component Doc Link
            const sublineData = getSublineData(value.hall, value.line, value.subline);
            const compData = sublineData._componentData?.[val] || {};
            updates.componentDocLink = compData.docLink || '';

            // --- ASSET PHOTO INHERITANCE ---
            // Check if there are specific assets (instances) for this component type
            const instances = sublineData._instances || [];
            const matchedInstance = instances.find(i => i.type === val);

            // --- ASSET PHOTO INHERITANCE (REFINED) ---
            // Don't auto-select instance/photo (per user request).
            // Logic moved to useEffect: photos will appear in 'Bibliotheek'.
            updates.instanceName = '';
            updates.instancePhoto = '';
            updates.taskImageUrls = [];
        } else if (field === 'task') {
            updates.tools = [];
            updates.pbms = [];
            // Preserve instance photo if available
            updates.taskImageUrls = value.instancePhoto ? [value.instancePhoto] : [];

            // Extract SOP Link(s) logic
            const meta = task_metadata[val] || {};

            // 1. Prepare SOPs
            updates.selectedSop = null;
            // Removed Auto-selection logic per user request. User must verify/choose manually.


            // 2. Initialize Custom Steps
            // Handle array of strings OR array of objects (Robust fallback)
            let defaultSteps = '';
            if (Array.isArray(meta.steps)) {
                console.log('Processing steps for task:', val, meta.steps); // DEBUG LOG
                defaultSteps = meta.steps
                    .filter(step => step) // Filter out null/undefined steps first
                    .map(step => {
                        if (typeof step === 'string') return step;
                        // Try all known keys
                        const text = step?.["Stap Omschrijving"] || step?.["omschrijving"] || step?.["text"] || step?.["description"] || step?.["step"];
                        if (text) return text;
                        // Last resort: stringify (better than [object Object])
                        return JSON.stringify(step);
                    }).filter(s => s).join('\n');
            }
            updates.customSteps = defaultSteps;

            // 3. Initialize Checks (Task Specific Defaults) WITH SORTING FROM LIBRARY
            let initialChecks = meta.checks || [];

            // Access library from data prop or context
            const library = data.mechanic_checks_library || {};

            // Determine sort order
            let flatLibrary = [];
            if (Array.isArray(library)) {
                flatLibrary = library;
            } else {
                // Flatten categorized object
                Object.values(library).forEach(catChecks => {
                    if (Array.isArray(catChecks)) flatLibrary.push(...catChecks);
                });
            }

            // Only sort if we have a valid library to sort against
            if (flatLibrary.length > 0) {
                initialChecks = [...initialChecks].sort((a, b) => {
                    const indexA = flatLibrary.indexOf(a);
                    const indexB = flatLibrary.indexOf(b);
                    // If both found, sort by index. If not found, put at end (stable sort for unknown items)
                    if (indexA !== -1 && indexB !== -1) return indexA - indexB;
                    if (indexA !== -1) return -1;
                    if (indexB !== -1) return 1;
                    return 0;
                });
            }

            updates.checks = initialChecks;

            // 4. Initialize Risks (Pre-select from Task Metadata)
            const initialRisks = (meta.risks || []).map(r => {
                // Handle both object format {Risico: "...", Beheersmaatregel: "..."} and string format
                return (typeof r === 'object' && r.Risico) ? r.Risico : r;
            });
            updates.risks = initialRisks;

            // 5. Initialize Safety Questions (Vinkjes)
            const initialSq = { ...(value.safetyQuestions || {}) };
            let sqChanged = false;

            const triggeredRisks = {};
            // New format
            if (meta.safetyQuestions) {
                Object.entries(meta.safetyQuestions).forEach(([key, val]) => {
                    if (val === true || val === 'Ja') triggeredRisks[key] = true;
                });
            }
            // Array format mapping
            else if (meta.risks && Array.isArray(meta.risks)) {
                meta.risks.forEach(r => {
                    const label = (typeof r === 'object' && r.Risico) ? r.Risico : r;
                    if (!label) return;
                    const lower = label.toLowerCase();
                    if (lower.includes('elektrisch') || lower.includes('spanning')) triggeredRisks['elektrische_spanning'] = true;
                    else if (lower.includes('druk') || lower.includes('pneumatisch') || lower.includes('hydrauliek')) triggeredRisks['restdruk'] = true;
                    else if (lower.includes('beweging') || lower.includes('beknelling') || lower.includes('mechanisch')) triggeredRisks['onverwachte_beweging'] = true;
                    else if (lower.includes('temperatuur') || lower.includes('heet') || lower.includes('koud')) triggeredRisks['gevaarlijke_temperaturen'] = true;
                    else if (lower.includes('stof') || lower.includes('chemisch')) triggeredRisks['contact_processtoffen'] = true;
                });
            }

            Object.keys(triggeredRisks).forEach(key => {
                if (initialSq[key] === undefined) {
                    initialSq[key] = true;
                    sqChanged = true;
                }
            });
            if (sqChanged) updates.safetyQuestions = initialSq;

            console.log('SOP Links found:', meta.sopLinks); // DEBUG LOG
        }

        onChange({ ...value, ...updates });
    };

    // --- Image Logic ---
    const [taskImages, setTaskImages] = React.useState([]);
    const [imageLoading, setImageLoading] = React.useState(false);
    const [sessionUploads, setSessionUploads] = React.useState([]); // Track uploads in this session

    // Load existing images when task changes
    useEffect(() => {
        if (value.hall && value.line && value.subline && value.component && value.task) {
            const loadTaskImages = async () => {
                setImageLoading(true);
                // Create a sanitized ID for the path - NOW INCLUDES LOCATION
                const locPrefix = value.location ? value.location.replace(/[^a-zA-Z0-9]/g, '_') : 'Unknown';
                const pathId = [locPrefix, value.hall, value.line, value.subline, value.component, value.task]
                    .map(s => s.replace(/[^a-zA-Z0-9]/g, '_'))
                    .join('_');

                try {
                    const { dataService } = await import('../../services/dataService');

                    // 1. Fetch Task Photos (Cloud)
                    let urls = await dataService.getTaskImages(pathId);

                    // 2. Fallback for legacy IDs
                    if ((!urls || urls.length === 0) && value.location) {
                        const legacyPathId = [value.hall, value.line, value.subline, value.component, value.task]
                            .map(s => s.replace(/[^a-zA-Z0-9]/g, '_'))
                            .join('_');
                        const legacyUrls = await dataService.getTaskImages(legacyPathId);
                        if (legacyUrls && legacyUrls.length > 0) urls = legacyUrls;
                    }

                    // 3. Inject Specific Asset Photos (from Hierarchy)
                    let instanceObjects = [];
                    try {
                        const getSublineData = (h, l, sl) => data.hierarchy?.[h]?.[l]?.[sl] || {};
                        const sublineData = getSublineData(value.hall, value.line, value.subline);

                        const instances = sublineData._instances || [];
                        instanceObjects = instances
                            .filter(i => i.type === value.component && i.photoUrl)
                            .map(i => ({
                                url: i.photoUrl,
                                label: i.name, // INJECT LABEL
                                type: 'asset'
                            }));
                    } catch (e) {
                        console.warn("Could not load instance photos", e);
                    }

                    // 4a. Inject PENDING Assets (From State - Unsaved)
                    const pendingObjects = (value.pendingAssets || [])
                        .filter(a =>
                            a.hall === value.hall &&
                            a.line === value.line &&
                            a.subline === value.subline &&
                            a.instanceData.type === value.component
                        )
                        .map(a => ({
                            url: a.instanceData.photoUrl,
                            label: a.instanceData.name,
                            type: 'asset'
                        }));


                    // 4b. Inject Session Reference Images (From State - Unsaved)
                    // These are images in taskImageUrls that are NOT in pendingAssets
                    // IMPORTANT: Also exclude LOTO photos to prevent mixing
                    const pendingUrls = new Set(pendingObjects.map(p => p.url));
                    const lotoPhotosSet = new Set(value.lotoPhotos || []);
                    const sessionReferenceUrls = (value.taskImageUrls || []).filter(u =>
                        !pendingUrls.has(u) && !lotoPhotosSet.has(u)
                    );

                    // 5. Merge All (Hier -> Pending -> Cloud -> Session)
                    const allItems = [
                        ...instanceObjects,
                        ...pendingObjects,
                        ...(urls || []).map(u => u), // Keep strings for regular photos
                        ...sessionReferenceUrls      // Add any other selected/uploaded images
                    ];

                    // Deduplicate by URL
                    const uniqueItems = [];
                    const seenUrls = new Set();
                    allItems.forEach(item => {
                        const u = typeof item === 'string' ? item : item.url;
                        if (!seenUrls.has(u)) {
                            seenUrls.add(u);
                            uniqueItems.push(item);
                        }
                    });


                    if (value.instancePhoto) {
                        const exists = seenUrls.has(value.instancePhoto);
                        if (!exists) uniqueItems.push(value.instancePhoto);
                    }

                    setTaskImages(uniqueItems);

                    // --- CRITICAL: Propagate Metadata to Wizard State ---
                    // This ensures SummaryStep knows the names of these assets
                    const newMetadata = { ...(value.imageMetadata || {}) };
                    let metaChanged = false;

                    // Helper to normalize keys
                    const normalize = (u) => {
                        try { return decodeURIComponent(u.split('?')[0]); } catch (e) { return u; }
                    };

                    // 1. Merge Asset Metadata (from uniqueItems)
                    uniqueItems.forEach(item => {
                        if (typeof item === 'object' && item.url && item.label) {
                            const key = item.url;
                            const normalizedKey = normalize(key);

                            // Set for both raw and normalized to be safe
                            if (newMetadata[key] !== item.label) {
                                newMetadata[key] = item.label;
                                metaChanged = true;
                            }
                            if (newMetadata[normalizedKey] !== item.label) {
                                newMetadata[normalizedKey] = item.label;
                                metaChanged = true;
                            }
                        }
                    });

                    // 2. Merge LOTO Metadata (Explicitly)
                    if (value.lotoMetadata) {
                        Object.entries(value.lotoMetadata).forEach(([url, meta]) => {
                            const label = meta.tagName || 'LOTO Punt';
                            const key = url;
                            const normalizedKey = normalize(key);

                            if (newMetadata[key] !== label) {
                                newMetadata[key] = label;
                                metaChanged = true;
                            }
                            if (newMetadata[normalizedKey] !== label) {
                                newMetadata[normalizedKey] = label;
                                metaChanged = true;
                            }
                        });
                    }

                    if (metaChanged) {
                        // Update parent state safely (dependencies won't trigger re-run)
                        // Use a timeout to avoid update-during-render warning if any
                        setTimeout(() => {
                            onChange({ ...value, imageMetadata: newMetadata });
                        }, 0);
                    }
                } catch (err) {
                    console.error("Error loading images:", err);
                } finally {
                    setImageLoading(false);
                }
            };
            loadTaskImages();
        } else {
            setTaskImages([]);
        }
    }, [value.hall, value.line, value.subline, value.component, value.task, value.location, data.hierarchy]);

    // --- Waste Option Logic (Derived State) ---
    const taskWasteMapping = data.task_waste_mapping || {};
    const currentWasteOption = value.task ? (taskWasteMapping[value.task] || '') : '';

    // --- Naming Queue State (Strict Mode) ---
    // --- Naming Queue State (Strict Mode) ---

    // --- INLINE NAMING LOGIC (Replaces Modal) ---
    // Track unnamed images via formData (value.unnamedImages).
    // If we have unnamed images, we block the Wizard and show inline inputs.

    const handleRename = async (url, name) => {
        if (!url || !name || !value.hall || !value.line || !value.subline || !value.component) return;
        const assetName = name.trim();

        try {
            // DEFERRED SAVE: Do not call dataService.addInstanceToHall yet.
            // Store it in pendingAssets for SummaryStep to process.
            const pendingAsset = {
                location: value.location || 'Doetinchem',
                hall: value.hall,
                line: value.line,
                subline: value.subline,
                instanceData: {
                    type: value.component,
                    name: assetName,
                    photoUrl: url,
                    createdAt: new Date().toISOString()
                }
            };

            // 1. Update Local State (UI) - Attach Label (Optimistic)
            setTaskImages(prev => prev.map(item => {
                const u = typeof item === 'string' ? item : item.url;
                const normalize = str => str ? str.split('?')[0] : '';

                if (normalize(u) === normalize(url)) {
                    return { url: url, label: assetName, type: 'asset' };
                }
                return item;
            }));

            // 2. Remove from Unnamed List & Add to Pending
            const currentUnnamed = value.unnamedImages || [];
            const newUnnamed = currentUnnamed.filter(u => u !== url);

            const currentPending = value.pendingAssets || [];
            const newPending = [...currentPending, pendingAsset];

            onChange({
                ...value,
                unnamedImages: newUnnamed,
                pendingAssets: newPending,
                instanceName: assetName // SAVE INSTANCE NAME TO WIZARD STATE
            });

        } catch (err) {
            console.error("Failed to queue asset:", err);
            alert(`Er ging iets mis bij het verwerken van de naam: ${err.message || err}`);
        }
    };

    const handleImageUpload = async (newUrls) => {

        if (newUrls && newUrls.length > 0) {
            // Updated Logic: Mark as unnamed immediately
            const currentUnnamed = value.unnamedImages || [];
            const newUnnamed = [...currentUnnamed, ...newUrls];

            // Standard Flow: Add to session & UI
            const updatedImages = [...taskImages, ...newUrls];
            setTaskImages(updatedImages);

            // Persist session uploads to Wizard State
            const currentSession = value.sessionUploadedUrls || [];
            const newSession = [...currentSession, ...newUrls];
            setSessionUploads(newSession);

            const currentSelection = value.taskImageUrls || [];
            onChange({
                ...value,
                taskImageUrls: [...currentSelection, ...newUrls],
                sessionUploadedUrls: newSession,
                unnamedImages: newUnnamed // MARK AS UNNAMED
            });

            if (setNewPhotoUploaded) setNewPhotoUploaded(true);

            if (setNewPhotoUploaded) setNewPhotoUploaded(true);

            // Persistence REMOVED: Defer to SummaryStep
            // try {
            //     await persistImages(updatedImages.map(i => typeof i === 'string' ? i : i.url));
            // } catch (e) {
            //     console.error("Persistence warning:", e);
            // }
        }
    };

    const handleImageToggle = (url) => {
        const currentSelection = value.taskImageUrls || [];
        let newSelection;
        if (currentSelection.includes(url)) {
            newSelection = currentSelection.filter(u => u !== url);
        } else {
            newSelection = [...currentSelection, url];
        }
        onChange({ ...value, taskImageUrls: newSelection });
    };

    // Derived state for dropdowns
    const availableLines = React.useMemo(() => {
        if (!value.hall || !data) return [];
        const hallData = data.hierarchy?.[value.hall];
        if (!hallData) return [];

        // 1. Try 'lines' sub-property (New Standard)
        if (hallData.lines) {
            return Object.keys(hallData.lines)
                .filter(key => key !== 'lines' && key !== 'id' && key !== 'name' && key !== 'sublines')
                .sort();
        }

        // 2. Fallback: Direct Keys (Old Standard / Flat)
        return Object.keys(hallData)
            .filter(key => {
                // Filter out metadata and known non-line keys
                if (key === 'type' || key === 'id' || key === 'name') return false;
                // Robust: Exclude anything that doesn't "look" like a line?
                // Or just trust the user structure.
                // Admin Dashboard typically filters by excluding keys starting with '_' or specific metadata.
                // Let's exclude common metadata.
                return true;
            })
            .sort();
    }, [value.hall, data]);

    // --- Image Deletion & Replacement Logic ---
    const [deleteModal, setDeleteModal] = React.useState({ isOpen: false, url: null, assetName: null, isSession: false });
    const fileInputRef = React.useRef(null);

    const handleImageDeleteClick = (url) => {
        // Identify if it's a session upload or stored asset logic
        const getFilename = (u) => {
            if (!u) return '';
            try { return decodeURIComponent(u.split('?')[0]).split('/').pop(); } catch (e) { return ''; }
        };
        const targetFilename = getFilename(url);
        const normalize = (u) => u ? u.split('?')[0] : '';
        const targetClean = normalize(url);

        const shouldRemove = (u) => {
            if (u === url) return true;
            if (normalize(u) === targetClean) return true;
            if (getFilename(u) === targetFilename) return true;
            return false;
        };

        const currentSession = value.sessionUploadedUrls || sessionUploads;
        const isSessionUpload = currentSession.some(u => shouldRemove(u));

        // Find asset label if possible
        const assetItem = taskImages.find(item => {
            const u = typeof item === 'string' ? item : item.url;
            return shouldRemove(u);
        });
        const assetName = (assetItem && typeof assetItem !== 'string') ? assetItem.label : null;

        setDeleteModal({
            isOpen: true,
            url: url,
            assetName: assetName,
            isSession: isSessionUpload
        });
    };

    const processDeletion = async (deleteLinkedEntities) => {
        const urlToDelete = deleteModal.url;
        if (!urlToDelete) return;

        const normalize = (u) => u ? u.split('?')[0] : '';
        const targetClean = normalize(urlToDelete);

        // Filter Logic
        const shouldRemove = (url) => normalize(url) === targetClean || url === urlToDelete;

        // 1. Update UI immediately
        const updatedImages = taskImages.filter(item => {
            const u = typeof item === 'string' ? item : item.url;
            return !shouldRemove(u);
        });
        setTaskImages(updatedImages);

        const currentSelection = value.taskImageUrls || [];
        const newSelection = currentSelection.filter(u => !shouldRemove(u));
        const currentSession = value.sessionUploadedUrls || sessionUploads;
        const newSession = currentSession.filter(u => !shouldRemove(u));

        onChange({
            ...value,
            taskImageUrls: newSelection,
            sessionUploadedUrls: newSession
        });
        setSessionUploads(newSession);

        setDeleteModal({ isOpen: false, url: null, assetName: null, isSession: false });

        // 2. Perform Backend Actions
        try {
            const { deleteImage, dataService } = await import('../../services/dataService');

            if (deleteLinkedEntities) {
                // DELETE ALL: Image + Asset + Workplans

                // A. Delete Permissions (Workplans)
                if (value.component) {
                    await dataService.deletePermitsByTaskImage(value.component, urlToDelete);
                }

                // B. Delete Asset from Hierarchy (if location context matches)
                if (value.hall && value.line && value.subline && value.component) {
                    await dataService.removeInstanceFromHall(
                        value.location || 'Doetinchem',
                        value.hall,
                        value.line,
                        value.subline,
                        urlToDelete
                    );
                }
            }

            // C. Always delete the actual file from storage (orphaned or replaced)
            await deleteImage(urlToDelete);

        } catch (error) {
            console.error("Error during deletion process:", error);
            // Non-blocking error for UI
        }
    };

    const handleReplaceClick = () => {
        // Trigger hidden file input
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const handleReplacementUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const oldUrl = deleteModal.url;

        setDeleteModal({ ...deleteModal, isOpen: false }); // Close modal primarily
        setImageLoading(true);

        try {
            const { uploadImage, dataService } = await import('../../services/dataService');

            // 1. Upload NEW Image
            const newUrl = await uploadImage(file, `replacements/${Date.now()}_${file.name}`);

            // 2. Update Backend References (Asset + Permits)
            // A. Update Asset in Hierarchy
            if (value.hall && value.line && value.subline && value.component) {
                await dataService.updateInstancePhoto(
                    value.location || 'Doetinchem',
                    value.hall,
                    value.line,
                    value.subline,
                    oldUrl,
                    newUrl
                );
            }

            // B. Update Workplans (Permits)
            await dataService.updatePermitsImageReference(oldUrl, newUrl);

            // C. Delete OLD Image from storage
            // We do this LAST to ensure we don't lose the old image if something fails above (though we accept the space cost risk)
            // Correction: We should strictly delete old only if replace succeeds.
            try {
                const { deleteImage } = await import('../../services/dataService');
                await deleteImage(oldUrl);
            } catch (delErr) {
                console.warn("Could not delete old image after replacement (non-fatal):", delErr);
            }

            // 3. Update UI State
            // Replace item in taskImages
            const normalize = (u) => u ? u.split('?')[0] : '';
            const targetClean = normalize(oldUrl);

            setTaskImages(prev => prev.map(item => {
                const u = typeof item === 'string' ? item : item.url;
                if (normalize(u) === targetClean) {
                    if (typeof item === 'string') return newUrl;
                    return { ...item, url: newUrl };
                }
                return item;
            }));

            // Replace in selection
            const currentSelection = value.taskImageUrls || [];
            if (currentSelection.includes(oldUrl)) {
                const newSel = currentSelection.map(u => u === oldUrl ? newUrl : u);
                onChange({ ...value, taskImageUrls: newSel });
            }

        } catch (err) {
            console.error("Replacement failed:", err);
            alert("Vervangen mislukt: " + err.message);
        } finally {
            setImageLoading(false);
            setDeleteModal({ isOpen: false, url: null, assetName: null, isSession: false });
            if (fileInputRef.current) fileInputRef.current.value = ''; // Reset input
        }
    };

    // Lazy load ImageUpload to avoid top-level import issues if any
    const ImageUpload = React.lazy(() => import('../common/ImageUpload'));

    // Helper to filter system keys
    const filterSystemKeys = (keys) => keys.filter(k => !['lines', 'sublines', 'halls', 'id', 'name'].includes(k));

    return (
        <div className="fade-in space-y-6">
            <h2 className="text-xl font-bold mb-4 text-white border-b border-slate-700 pb-2">Stap 2: Locatie & Taak</h2>

            <HierarchySelector
                hall={value.hall || ''}
                line={value.line || ''}
                subline={value.subline || ''}
                component={value.component || ''}
                task={value.task || ''}
                halls={halls}
                lines={availableLines}
                sublines={sublines}
                components={components}
                tasks={tasks}
                sublineDocLink={value.sublineDocLink}
                sublineDocLinkElectro={value.sublineDocLinkElectro}
                sublineDocLinkManual={value.sublineDocLinkManual}
                componentDocLink={value.componentDocLink}
                instanceName={value.instanceName}
                onChange={handleChange}
                filterSystemKeys={filterSystemKeys}
            />

            {/* SOP & Steps Section */}
            {value.task && (() => {
                const meta = task_metadata[value.task] || {};
                let sopLinks = meta.sopLinks || (meta.sopLink ? [{ name: 'Standaard SOP', url: meta.sopLink }] : []);

                // Filter by Location if applicable
                // Logic: If allowedLocations is present and NOT empty, current location MUST be in it.
                // If allowedLocations is undefined or empty, it's Global (visible to all).
                if (value.location) {
                    sopLinks = sopLinks.filter(sop => {
                        if (!sop) return false;
                        if (!sop.allowedLocations || sop.allowedLocations.length === 0) return true;
                        return sop.allowedLocations.includes(value.location);
                    });
                }

                return (
                    <div className="mt-4 space-y-4 animate-in fade-in slide-in-from-top-4 duration-300">

                        {/* SOP Selection */}
                        {sopLinks.length > 0 && (
                            <div className="bg-slate-800/50 p-3 rounded border border-slate-700">
                                <div className="flex justify-between items-center mb-2">
                                    <h3 className="text-sm font-bold text-slate-300">📋 SOP / Werkinstructie</h3>
                                    {value.selectedSop && (
                                        <a
                                            href={value.selectedSop.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="btn btn-xs btn-primary gap-1"
                                        >
                                            Open Instructie ↗
                                        </a>
                                    )}
                                </div>

                                {sopLinks.length > 0 && (
                                    <select
                                        className="input glass-input w-full text-sm"
                                        value={value.selectedSop?.url || ''}
                                        onChange={(e) => {
                                            const selected = sopLinks.find(s => s.url === e.target.value);
                                            onChange({ ...value, selectedSop: selected });
                                        }}
                                    >
                                        <option value="">-- Selecteer specifieke instructie (Optioneel) --</option>
                                        {sopLinks.map((sop, idx) => (
                                            <option key={idx} value={sop.url}>{sop.name}</option>
                                        ))}
                                    </select>
                                )}
                            </div>
                        )}


                        {/* Editable Work Steps */}
                        <div className="bg-slate-800/50 p-3 rounded border border-slate-700">
                            <div className="flex justify-between items-center mb-2">
                                <h3 className="text-sm font-bold text-slate-300">🛠️ Werkstappen (Aanpasbaar)</h3>
                                <span className="text-[10px] text-slate-500 uppercase tracking-wider">Specifiek voor deze vergunning</span>
                            </div>
                            <textarea
                                className="textarea glass-input w-full min-h-[300px] text-sm font-mono leading-relaxed resize-y"
                                rows={10}
                                placeholder="Geen standaard stappen gedefinieerd. Voeg hier stappen toe..."
                                value={value.customSteps || ''}
                                onChange={(e) => onChange({ ...value, customSteps: e.target.value })}
                            />
                        </div>

                        {/* --- NEW: Editable Mechanic Checks --- */}
                        <div className="bg-slate-800/50 p-3 rounded border border-slate-700">
                            <div className="flex justify-between items-center mb-4">
                                <h3 className="text-sm font-bold text-slate-300">✅ Taak Specifieke Checks</h3>
                                <div className="flex gap-2">
                                    {/* Library Dropdown */}
                                    <select
                                        className="select select-xs glass-input max-w-[200px]"
                                        onChange={(e) => {
                                            const selectedCheck = e.target.value;
                                            if (selectedCheck) {
                                                let currentChecks = value.checks ? [...value.checks] : [];
                                                if (!currentChecks.includes(selectedCheck)) {
                                                    currentChecks.push(selectedCheck);

                                                    // SORT
                                                    const library = data.mechanic_checks_library || {};
                                                    let flatLibrary = [];
                                                    if (Array.isArray(library)) {
                                                        flatLibrary = library;
                                                    } else {
                                                        Object.values(library).forEach(catChecks => {
                                                            if (Array.isArray(catChecks)) flatLibrary.push(...catChecks);
                                                        });
                                                    }

                                                    if (flatLibrary.length > 0) {
                                                        currentChecks.sort((a, b) => {
                                                            const indexA = flatLibrary.indexOf(a);
                                                            const indexB = flatLibrary.indexOf(b);
                                                            if (indexA !== -1 && indexB !== -1) return indexA - indexB;
                                                            if (indexA !== -1) return -1;
                                                            if (indexB !== -1) return 1;
                                                            return 0;
                                                        });
                                                    }

                                                    onChange({ ...value, checks: currentChecks });
                                                }
                                                e.target.value = ""; // Reset dropdown
                                            }
                                        }}
                                        value=""
                                    >
                                        <option value="" disabled>+ Uit Bibliotheek</option>
                                        {(() => {
                                            const library = data.mechanic_checks_library || {};

                                            // Handle Array (Legacy/Migration)
                                            if (Array.isArray(library)) {
                                                return library.map(check => (
                                                    <option key={check} value={check}>{check}</option>
                                                ));
                                            }

                                            // Handle Categorized Object (New Standard)
                                            return Object.entries(library).map(([category, checks]) => (
                                                <optgroup key={category} label={category}>
                                                    {checks.map(check => (
                                                        <option key={check} value={check}>{check}</option>
                                                    ))}
                                                </optgroup>
                                            ));
                                        })()}
                                    </select>

                                    {/* Custom Add Button */}
                                    <button
                                        onClick={() => {
                                            const newCheck = prompt("Nieuwe check invoeren:");
                                            if (newCheck && newCheck.trim()) {
                                                const currentChecks = value.checks || [];
                                                onChange({ ...value, checks: [...currentChecks, newCheck.trim()] });
                                            }
                                        }}
                                        className="btn btn-xs btn-outline btn-primary"
                                    >
                                        + Eigen Check
                                    </button>
                                </div>
                            </div>

                            <div className="space-y-2">
                                {(value.checks && value.checks.length > 0) ? (
                                    value.checks.map((check, idx) => (
                                        <div key={idx} className="flex items-center gap-2 bg-slate-900 border border-slate-700 p-2 rounded group">
                                            {/* Reorder Controls */}
                                            <div className="flex flex-col gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                                                <button
                                                    className="text-slate-500 hover:text-blue-400 leading-none text-xs"
                                                    disabled={idx === 0}
                                                    onClick={() => {
                                                        if (idx > 0) {
                                                            const newChecks = [...value.checks];
                                                            [newChecks[idx - 1], newChecks[idx]] = [newChecks[idx], newChecks[idx - 1]];
                                                            onChange({ ...value, checks: newChecks });
                                                        }
                                                    }}
                                                >
                                                    ▲
                                                </button>
                                                <button
                                                    className="text-slate-500 hover:text-blue-400 leading-none text-xs"
                                                    disabled={idx === value.checks.length - 1}
                                                    onClick={() => {
                                                        if (idx < value.checks.length - 1) {
                                                            const newChecks = [...value.checks];
                                                            [newChecks[idx + 1], newChecks[idx]] = [newChecks[idx], newChecks[idx + 1]];
                                                            onChange({ ...value, checks: newChecks });
                                                        }
                                                    }}
                                                >
                                                    ▼
                                                </button>
                                            </div>

                                            <span className="text-sm text-slate-300 flex-1">{check}</span>

                                            <button
                                                onClick={() => {
                                                    const newChecks = value.checks.filter((_, i) => i !== idx);
                                                    onChange({ ...value, checks: newChecks });
                                                }}
                                                className="text-slate-600 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
                                                title="Verwijder check"
                                            >
                                                🗑️
                                            </button>
                                        </div>
                                    ))
                                ) : (
                                    <div className="text-sm text-slate-500 italic text-center py-2">
                                        Geen checks geselecteerd.
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                );
            })()}



            {/* Image Upload Section - Only visible when task is selected */}
            {value.task && (
                <div className="mt-6 p-4 bg-slate-800/50 rounded-lg border border-slate-700 animate-fade-in">
                    <div className="flex justify-between items-center mb-3">
                        <h3 className="text-sm font-bold text-slate-300">📸 Asset/Onderdeel foto (Bibliotheek)</h3>
                        {value.taskImageUrls && value.taskImageUrls.length > 0 ? (
                            <span className="text-xs text-green-400 font-bold bg-green-900/30 px-2 py-1 rounded border border-green-700/50">
                                ✓ {value.taskImageUrls.length} Foto('s) geselecteerd
                            </span>
                        ) : (
                            <span className="text-xs text-red-400 font-bold animate-pulse bg-red-900/30 px-2 py-1 rounded border border-red-700/50">
                                ⚠️ Selecteer minimaal 1 foto
                            </span>
                        )}
                    </div>

                    <React.Suspense fallback={<div className="text-xs text-slate-500">Laden...</div>}>
                        <ImageUpload
                            label="Selecteer een foto uit de bibliotheek of upload een nieuwe"
                            path={`tasks/${[value.hall, value.line, value.subline, value.component, value.task].map(s => s.replace(/[^a-zA-Z0-9]/g, '_')).join('_')}`}
                            onUploadComplete={handleImageUpload}
                            onDelete={handleImageDeleteClick}
                            onSessionDelete={(url) => {
                                // Remove from all session-related state arrays without opening modal
                                setTaskImages(prev => prev.filter(u => {
                                    const uUrl = typeof u === 'string' ? u : u.url;
                                    return uUrl !== url;
                                }));
                                onChange({
                                    ...value,
                                    sessionUploadedUrls: (value.sessionUploadedUrls || []).filter(u => u !== url),
                                    unnamedImages: (value.unnamedImages || []).filter(u => u !== url),
                                    taskImageUrls: (value.taskImageUrls || []).filter(u => u !== url)
                                });
                            }}
                            currentImageUrls={taskImages}
                            availableImages={taskImages}
                            selectedUrls={value.taskImageUrls || []}
                            onToggleSelect={handleImageToggle}
                            allowDelete={false}
                            sessionUploadedUrls={value.sessionUploadedUrls || sessionUploads}
                            excludeUrls={value.lotoPhotos || []}
                            // INLINE NAMING PROPS
                            unnamedImages={value.unnamedImages || []}
                            onRename={handleRename}
                            // TECH DOC DISCREPANCY PROPS
                            showDiscrepancyUI={true}
                            photoMetadata={value.assetMetadata || {}}
                            onMetadataChange={(url, metadata) => {
                                onChange({
                                    ...value,
                                    assetMetadata: {
                                        ...(value.assetMetadata || {}),
                                        [url]: metadata
                                    }
                                });
                            }}
                        />
                    </React.Suspense>

                    {(!value.taskImageUrls || value.taskImageUrls.length === 0) && (
                        <p className="text-xs text-red-300/70 mt-2">
                            Selecteer een foto uit de bibliotheek of upload een nieuwe.
                            Deze foto komt op de werkvergunning.
                        </p>
                    )}
                </div>
            )}



            {React.useEffect(() => {
                if (!value.task || !data.task_metadata) return;

                // If we've already synced this specific task, don't auto-reset it again
                // (This allows the user to manually uncheck things without them popping back)
                if (lastSyncedTask.current === value.task) return;

                const triggeredRisks = {};
                const currentQuestions = value.safetyQuestions || {};

                // 1. Load from Component-Specific Library
                let meta = null;
                const componentLibrary = data.onderdeel_tasks?.[value.component];

                if (componentLibrary) {
                    const isInLibrary = Array.isArray(componentLibrary)
                        ? componentLibrary.includes(value.task)
                        : (componentLibrary[value.task] !== undefined);

                    if (isInLibrary) {
                        meta = data.task_metadata?.[value.task];
                        if (!meta && !Array.isArray(componentLibrary)) {
                            meta = componentLibrary[value.task];
                        }
                    }
                }

                if (meta) {
                    // Pre-population Logic
                    if (meta.safetyQuestions) {
                        Object.entries(meta.safetyQuestions).forEach(([key, val]) => {
                            if (val === true || val === 'Ja') triggeredRisks[key] = true;
                        });
                    } else if (meta.risks && Array.isArray(meta.risks)) {
                        meta.risks.forEach(r => {
                            const label = (typeof r === 'object' && r.Risico) ? r.Risico : r;
                            if (!label) return;
                            const lower = label.toLowerCase();
                            if (lower.includes('elektrisch') || lower.includes('spanning')) triggeredRisks['elektrische_spanning'] = true;
                            else if (lower.includes('druk') || lower.includes('pneumatisch') || lower.includes('hydrauliek')) triggeredRisks['restdruk'] = true;
                            else if (lower.includes('beweging') || lower.includes('beknelling') || lower.includes('mechanisch')) triggeredRisks['onverwachte_beweging'] = true;
                            else if (lower.includes('temperatuur') || lower.includes('heet') || lower.includes('koud')) triggeredRisks['gevaarlijke_temperaturen'] = true;
                            else if (lower.includes('stof') || lower.includes('chemisch')) triggeredRisks['contact_processtoffen'] = true;
                        });
                    }

                    // On the very first sync for this task, we can be aggressive.
                    // If we find any metadata, we apply it.
                    if (Object.keys(triggeredRisks).length > 0) {
                        lastSyncedTask.current = value.task;
                        onChange({
                            ...value,
                            safetyQuestions: { ...currentQuestions, ...triggeredRisks }
                        });
                    }
                }

            }, [value.task, value.component, data.task_metadata, data.onderdeel_tasks])}

            {/* --- ENERGY & PROCESS RISKS (Moved from Step 4) --- */}
            {/* Only visible when task is selected */}
            {value.task && (
                <div className="mt-8 border-t border-slate-700 pt-6 animate-fade-in">
                    <div className="bg-slate-900/50 p-6 rounded-xl border border-slate-700/50">
                        <h2 className="text-xl font-bold mb-4 text-yellow-500 border-b border-slate-700 pb-2 flex items-center gap-2">
                            ⚡ Stap 2b: Energie & Proces Risico's
                        </h2>
                        <p className="text-sm text-slate-400 mb-4">
                            Vink aan welke risico's van toepassing zijn. Dit bepaalt welke LOTO-procedures vereist zijn.
                        </p>
                        <div className="grid grid-cols-1 gap-3">
                            {SAFETY_QUESTIONS.energy.map(q => (
                                <label key={q.id} className="flex items-start gap-3 cursor-pointer hover:bg-slate-800/50 p-3 rounded transition border border-transparent hover:border-slate-600 bg-slate-900/30">
                                    <input
                                        type="checkbox"
                                        checked={value.safetyQuestions?.[q.id] || false}
                                        onChange={(e) => {
                                            const newSq = { ...value.safetyQuestions, [q.id]: e.target.checked };
                                            onChange({ ...value, safetyQuestions: newSq });
                                        }}
                                        className="w-5 h-5 mt-0.5 rounded border-slate-600 bg-slate-800 text-yellow-500 focus:ring-yellow-500 focus:ring-offset-slate-900"
                                    />
                                    <span className="text-sm leading-relaxed text-slate-300 font-medium">{q.label}</span>
                                </label>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* LOTO SECTION (Merged from LOTOStep) - Only show if TASK is selected */}
            {value.task && (
                <div className="mt-8 border-t border-slate-700 pt-6 animate-fade-in">
                    <div className="bg-slate-900/50 p-6 rounded-xl border border-slate-700/50">
                        <h2 className="text-xl font-bold mb-4 text-white border-b border-slate-700 pb-2 flex items-center gap-2">
                            🔒 Stap 2c: Veiligheid & LOTO
                        </h2>

                        {/* LOTO Requirement Warning */}
                        {lotoContext.lotoRequired && (
                            <div className="bg-yellow-900/20 border border-yellow-700/50 p-4 rounded-lg mb-6">
                                <h3 className="font-bold text-yellow-500 flex items-center gap-2 mb-2">
                                    ⚠️ LOTO Procedures Vereist ({lotoContext.lotoSteps?.length || 0})
                                </h3>
                                <p className="text-sm text-yellow-200/80 mb-4">
                                    Voor deze taak zijn veiligheidsprocedures van kracht.
                                    Upload per procedure een <strong>instructiefoto</strong>.
                                </p>
                                <ul className="space-y-2">
                                    {lotoContext.lotoSteps?.map((step, idx) => (
                                        <li key={idx} className="flex items-start gap-2 text-sm text-slate-300 bg-slate-800/50 p-2 rounded">
                                            <span className="font-bold text-yellow-500">{idx + 1}.</span>
                                            <span>
                                                <strong className="text-yellow-400/80 uppercase text-xs">[{step.type}]</strong>
                                                {' '}{step.description}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {/* LOTO Upload Section */}
                        <div className="">
                            <div className="flex justify-between items-center mb-4">
                                <h3 className="font-bold text-slate-300">📸 LOTO Instructiefoto's</h3>
                                {/* Status Badge */}
                                {(value.lotoPhotos?.length || 0) >= (lotoContext.lotoSteps?.length || 0) ? (
                                    <span className="text-xs text-green-400 font-bold bg-green-900/30 px-2 py-1 rounded border border-green-700/50">
                                        ✓ Compleet ({value.lotoPhotos?.length})
                                    </span>
                                ) : (
                                    <span className="text-xs text-red-400 font-bold animate-pulse bg-red-900/30 px-2 py-1 rounded border border-red-700/50">
                                        ⚠️ Nog {Math.max(0, (lotoContext.lotoSteps?.length || 0) - (value.lotoPhotos?.length || 0))} nodig
                                    </span>
                                )}
                            </div>

                            {/* We use a separate ImageUpload instance for LOTO to keep it distinct */}
                            <React.Suspense fallback={<div className="text-xs text-slate-500">Laden...</div>}>
                                <ImageUpload
                                    path={`loto/loto_${[value.location, value.hall, value.line, value.subline, value.component, value.task].map(s => s ? s.replace(/[^a-zA-Z0-9]/g, '_') : '').join('_')}`}
                                    label="📸 Upload LOTO Foto"

                                    // Logic: Use lotoPhotos as selected
                                    // For 'currentImageUrls' (available), we construct objects from the selection to show labels
                                    currentImageUrls={(value.lotoPhotos || []).map(url => ({
                                        url,
                                        label: value.lotoMetadata?.[url]?.tagName || 'LOTO Punt'
                                    }))}
                                    selectedUrls={value.lotoPhotos || []}

                                    // Handlers
                                    onUploadComplete={(newUrls) => {
                                        // 1. Add to Session & Unnamed
                                        const currentUnnamed = value.unnamedImages || [];
                                        const newUnnamed = [...currentUnnamed, ...newUrls];

                                        const currentSession = value.sessionUploadedUrls || [];
                                        const newSession = [...currentSession, ...newUrls];

                                        // 2. Add to LotoPhotos (Crucial for Validation!)
                                        const currentLoto = value.lotoPhotos || [];
                                        const newLoto = [...currentLoto, ...newUrls];

                                        // 3. Propagate Changes
                                        // Update all three fields: session (for cleanup), unnamed (for prompts), lotoPhotos (for logic)
                                        onChange({
                                            ...value,
                                            unnamedImages: newUnnamed,
                                            sessionUploadedUrls: newSession,
                                            lotoPhotos: newLoto
                                        });

                                        if (setNewPhotoUploaded) setNewPhotoUploaded(true);
                                    }}

                                    onDelete={(url) => {
                                        // Remove from lotoPhotos
                                        const newLoto = (value.lotoPhotos || []).filter(u => u !== url);
                                        // Remove from unnamed if present
                                        const newUnnamed = (value.unnamedImages || []).filter(u => u !== url);

                                        onChange({
                                            ...value,
                                            lotoPhotos: newLoto,
                                            unnamedImages: newUnnamed
                                        });
                                    }}

                                    onSessionDelete={(url) => {
                                        // Remove from all LOTO-related state arrays without opening modal
                                        onChange({
                                            ...value,
                                            lotoPhotos: (value.lotoPhotos || []).filter(u => u !== url),
                                            sessionUploadedUrls: (value.sessionUploadedUrls || []).filter(u => u !== url),
                                            unnamedImages: (value.unnamedImages || []).filter(u => u !== url)
                                        });
                                    }}

                                    // LOTO Naming
                                    unnamedImages={value.unnamedImages || []}
                                    onRename={(url, name) => {
                                        if (!name) return;
                                        // Update Metadata - PRESERVE existing discrepancy fields
                                        const meta = value.lotoMetadata || {};
                                        const existingMeta = meta[url] || {};
                                        const newMeta = {
                                            ...meta,
                                            [url]: {
                                                ...existingMeta,
                                                tagName: name
                                            }
                                        };

                                        // Remove from unnamed
                                        const newUnnamed = (value.unnamedImages || []).filter(u => u !== url);

                                        onChange({
                                            ...value,
                                            lotoMetadata: newMeta,
                                            unnamedImages: newUnnamed
                                        });
                                    }}

                                    // TECH DOC DISCREPANCY PROPS
                                    showDiscrepancyUI={true}
                                    photoMetadata={value.lotoMetadata || {}}
                                    onMetadataChange={(url, metadata) => {
                                        const meta = value.lotoMetadata || {};
                                        onChange({
                                            ...value,
                                            lotoMetadata: {
                                                ...meta,
                                                [url]: {
                                                    ...(meta[url] || {}),
                                                    ...metadata
                                                }
                                            }
                                        });
                                    }}

                                    allowDelete={true}
                                    sessionUploadedUrls={value.sessionUploadedUrls || sessionUploads}
                                />
                            </React.Suspense>
                        </div>
                    </div>
                </div>
            )}

            {/* Hidden Input for Replacements */}
            <input
                type="file"
                ref={fileInputRef}
                style={{ display: 'none' }}
                accept="image/*"
                onChange={handleReplacementUpload}
            />

            {/* DELETE MODAL */}
            <DeleteImageModal
                isOpen={deleteModal.isOpen}
                url={deleteModal.url}
                assetName={deleteModal.assetName}
                isSession={deleteModal.isSession}
                loading={imageLoading}
                onReplace={handleReplaceClick}
                onDelete={processDeletion}
                onClose={() => setDeleteModal({ ...deleteModal, isOpen: false })}
            />
        </div>
    );
}

